// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React from 'react';
import {
  Table,
  Space,
  Typography,
  Avatar,
  Tag,
  Popconfirm,
  Button,
  Spin,
  Alert,
} from 'antd';
import { User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { Personnel, PersonnelStatus } from '../types/personnel';
import { FilterSection } from '../components/common/FilterSection';
import { Pagination } from '../components/common/Pagination';
import { ActionMenu, ActionMenuHandlers } from '../components/common/ActionMenu';
import { AddPersonnelModal, ManagerOption, Role } from '../components/personnel/AddPersonnelModal';
import { EditPersonnelModal } from '../components/personnel/EditPersonnelModal';
import { formatRole } from '../utils/formatters';
import { buildFullUrl } from '../contexts/AvatarContext';
import { usePersonnelManagement } from '@/features/personnel/usePersonnelManagement';
import badgePlus from '../assets/badge-plus.svg';
import styles from './PersonnelManagementPage.module.css';
import { useAuth } from '@/contexts/AuthContext';
import { can } from '@shared/utils';
import { apiService } from '@/services/api.service';

const { Text } = Typography;

// Component để render avatar với auth
interface PersonnelAvatarProps {
  avatarUrl: string | null | undefined;
  fetchAvatarAsDataUri: (url: string) => Promise<string | null>;
  cacheRef: React.MutableRefObject<Map<string, string>>;
}

const PersonnelAvatar: React.FC<PersonnelAvatarProps> = ({ avatarUrl, fetchAvatarAsDataUri, cacheRef }) => {
  const [avatarDataUri, setAvatarDataUri] = React.useState<string | null>(null);
  const [avatarError, setAvatarError] = React.useState(false);
  const [useDirectUrl, setUseDirectUrl] = React.useState(false);

  React.useEffect(() => {
    // Reset state khi avatarUrl thay đổi
    setAvatarDataUri(null);
    setAvatarError(false);
    setUseDirectUrl(false);

    if (!avatarUrl) {
      return;
    }

    const isValidUrl = avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://');
    if (!isValidUrl) {
      return;
    }

    // Check cache first
    if (cacheRef.current.has(avatarUrl)) {
      const cached = cacheRef.current.get(avatarUrl);
      if (cached) {
        setAvatarDataUri(cached);
        setAvatarError(false);
        return;
      }
    }

    // Fetch avatar với error handling
    fetchAvatarAsDataUri(avatarUrl)
      .then((dataUri) => {
        if (dataUri) {
          // Cache the result
          cacheRef.current.set(avatarUrl, dataUri);
          setAvatarDataUri(dataUri);
          setAvatarError(false);
          setUseDirectUrl(false);
        } else {
          // Fallback: dùng URL trực tiếp nếu fetch data URI thất bại
          setUseDirectUrl(true);
          setAvatarError(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching avatar:', error);
        // Fallback: dùng URL trực tiếp
        setUseDirectUrl(true);
        setAvatarError(false);
      });
  }, [avatarUrl, fetchAvatarAsDataUri, cacheRef]);

  // Sử dụng data URI nếu có, nếu không thì dùng URL trực tiếp (fallback)
  const avatarSrc = avatarDataUri && !avatarError ? avatarDataUri : (useDirectUrl && avatarUrl ? avatarUrl : undefined);
  const showAvatar = !!avatarSrc;

  return (
    <Avatar 
      size={32} 
      src={showAvatar ? avatarSrc : undefined}
      icon={!showAvatar ? <User size={16} /> : undefined}
      onError={() => {
        // Nếu đang dùng data URI mà lỗi, thử fallback về URL trực tiếp
        if (avatarDataUri && !useDirectUrl) {
          setUseDirectUrl(true);
          setAvatarDataUri(null);
          setAvatarError(false);
        } else {
          setAvatarError(true);
        }
        return false;
      }}
    />
  );
};

export const PersonnelManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAssignRole = user?.role === 'admin';
  const canAssignRoleOnCreate = canAssignRole || user?.role === 'hr';
  const fixedCreateDepartmentId = user?.departmentId && user.permissions?.some(
    (rule) => rule.resource === 'user' && rule.action === 'create' &&
      rule.conditions?.departmentId === user.departmentId,
  ) ? user.departmentId : undefined;
  const [assignableRoles, setAssignableRoles] = React.useState<Role[]>([]);
  const [managerCandidates, setManagerCandidates] = React.useState<ManagerOption[]>([]);
  React.useEffect(() => {
    if (!canAssignRoleOnCreate) return;
    const rolesEndpoint = canAssignRole ? '/rbac/roles' : '/rbac/roles/assignable';
    apiService.get<Array<{ id: number; name: string }>>(rolesEndpoint)
      .then((items) => setAssignableRoles(items.map((role) => ({
        id: String(role.id),
        name: formatRole(role.name),
        code: role.name,
      }))))
      .catch(() => setAssignableRoles([]));
  }, [canAssignRole, canAssignRoleOnCreate]);
  React.useEffect(() => {
    if (!user) return;
    apiService.get<ManagerOption[]>('/users/managers')
      .then(setManagerCandidates)
      .catch(() => setManagerCandidates([]));
  }, [user?.id]);
  const canCreatePersonnel = can(user, 'user', 'create');
  const canReadPersonnel = can(user, 'user', 'read');
  const canUpdatePersonnel = can(user, 'user', 'update');
  const canDeletePersonnel = can(user, 'user', 'delete');
  const canBlockPersonnel = can(user, 'user', 'block');
  const scopedDepartmentId = user?.permissions?.find(
    (rule) => rule.resource === 'user' &&
      (rule.action === 'list' || rule.action === 'read') &&
      rule.conditions?.departmentId !== undefined,
  )?.conditions?.departmentId;
  const isDepartmentScoped = user?.departmentId !== undefined &&
    Number(scopedDepartmentId) === user.departmentId;
  const updateRules = (user?.permissions ?? []).filter(
    (rule) => rule.resource === 'user' && rule.action === 'update',
  );
  const editablePersonnelFields = canAssignRole || can(user, 'all', 'manage') ||
    updateRules.some((rule) => !rule.fields?.length)
    ? undefined
    : [...new Set(updateRules.flatMap((rule) => rule.fields ?? []))];
  
  const {
    searchText,
    setSearchText,
    selectedDepartment,
    setSelectedDepartment,
    filteredData,
    loading,
    error,
    departments,
    total,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    isAddModalOpen,
    isEditModalOpen,
    selectedPersonnel,
    handleClearFilter,
    handleAddPersonnel,
    handleCloseAddModal,
    handleSubmitAddPersonnel,
    handleEditPersonnel,
    handleCloseEditModal,
    handleSubmitEditPersonnel,
    handleDeletePersonnel,
    handleLockPersonnel,
    handleApproveRequest,
    saveFilterStateBeforeNavigate,
    usersWithPendingRequests,
    deletingId,
    lockingId,
    avatarCacheRef,
    fetchAvatarAsDataUri,
  } = usePersonnelManagement();

  const filterDepartments = isDepartmentScoped
    ? departments.filter((department) => department.id === user?.departmentId)
    : departments;
  const allDepartmentsLabel = isDepartmentScoped
    ? 'Phòng ban của tôi'
    : 'Tất cả phòng ban';

  React.useEffect(() => {
    if (!isDepartmentScoped) return;
    const ownDepartmentName = filterDepartments[0]?.name;
    const selectionIsAllowed =
      selectedDepartment === allDepartmentsLabel ||
      (ownDepartmentName !== undefined && selectedDepartment === ownDepartmentName);
    if (!selectionIsAllowed) setSelectedDepartment(allDepartmentsLabel);
  }, [
    allDepartmentsLabel,
    filterDepartments,
    isDepartmentScoped,
    selectedDepartment,
    setSelectedDepartment,
  ]);

  // Delete button refs để trigger Popconfirm
  const deleteButtonRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const lockButtonRefs = React.useRef<Record<string, HTMLElement | null>>({});

  // Render status tag cho từng nhân viên
  const getStatusTag = (status: PersonnelStatus) => {
    if (status === 'active') {
      return (
        <Tag className={styles.statusTagActive}>
          Hoạt động
        </Tag>
      );
    }
    if (status === 'unknown') {
      return <Tag className={styles.statusTagUnknown}>Không rõ</Tag>;
    }
    return (
      <Tag className={styles.statusTagLocked}>
        Đã khoá
      </Tag>
    );
  };

  // Handler để trigger delete Popconfirm
  const handleDeleteClick = (record: Personnel) => {
    setTimeout(() => {
      deleteButtonRefs.current[record.id]?.click();
    }, 0);
  };

  // Handler để trigger lock Popconfirm
  const handleLockClick = (record: Personnel) => {
    setTimeout(() => {
      lockButtonRefs.current[record.id]?.click();
    }, 0);
  };

  // Handler để navigate đến detail page với update request
  const handleApproveRequestNavigate = async (record: Personnel) => {
    const updateRequest = await handleApproveRequest(record);
    if (updateRequest) {
      saveFilterStateBeforeNavigate();
      navigate(`/management/personnel/detail/${record.employeeCode}`, {
        state: { 
          personnel: record,
          updateRequestId: updateRequest.id,
          refresh: true,
          timestamp: Date.now(),
        },
      });
    }
  };

  // Action menu handlers - xử lý các hành động từ menu
  const handleActionMenu: ActionMenuHandlers<Personnel> = {
    onView: (record) => {
      saveFilterStateBeforeNavigate();
      navigate(`/management/personnel/detail/${record.employeeCode}`, {
        state: { personnel: record },
      });
    },
    onUpdate: (record) => {
      saveFilterStateBeforeNavigate();
      navigate('/management/personnel/update-personal-info', {
        state: { personnel: record },
      });
    },
    onApprove: handleApproveRequestNavigate,
    onEdit: handleEditPersonnel,
    onManageSession: () => {},
    onLock: handleLockClick,
    onDelete: handleDeleteClick,
  };

  // Columns định nghĩa cấu trúc bảng nhân sự
  const columns: ColumnsType<Personnel> = [
    {
      title: 'Mã NS',
      dataIndex: 'employeeCode',
      key: 'employeeCode',
      width: 100,
    },
    {
      title: 'Họ và tên',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      render: (text, record) => {
        const avatarUrl = buildFullUrl(record.avatar);
        const hasPendingRequest = usersWithPendingRequests.has(parseInt(record.id));
        
        return (
          <Space>
            <PersonnelAvatar 
              avatarUrl={avatarUrl}
              fetchAvatarAsDataUri={fetchAvatarAsDataUri}
              cacheRef={avatarCacheRef}
            />
            <Space>
              <Text>{text}</Text>
              {hasPendingRequest && (
                <span className={styles.pendingRequestBadge} title="Có yêu cầu cập nhật thông tin đang chờ duyệt">
                  ⚠️
                </span>
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Chức vụ',
      dataIndex: 'position',
      key: 'position',
      width: 120,
      render: (text: string, record: Personnel) => {
        const assignedRole = assignableRoles.find(
          (role) => role.id === String(record.roleId),
        );
        return assignedRole?.name ?? formatRole(text);
      },
    },
    {
      title: 'Công ty/ Chi nhánh, phòng ban',
      dataIndex: 'company',
      key: 'company',
      width: 250,
    },
    {
      title: 'Vị trí',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      width: 150,
      render: (text, record) => {
        if (record.role === 'admin') {
          return null;
        }
        return (
          <span className={styles.jobTitleText}>{text}</span>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Ngày vào',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
    },
    ...(canReadPersonnel || canUpdatePersonnel || canDeletePersonnel || canBlockPersonnel
      ? ([{
      title: 'Hành động',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <div className={styles.actionCell}>
          {canDeletePersonnel && <Popconfirm
            title={null}
            icon={null}
            description="Bạn có chắc chắn muốn xóa nhân sự này?"
            onConfirm={() => handleDeletePersonnel(record)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ 
              danger: true,
              loading: deletingId === record.id 
            }}
            placement="left"
            overlayStyle={{ marginLeft: '-20px' }}
            getPopupContainer={(triggerNode) => {
              const cell = triggerNode.closest('td');
              return cell || document.body;
            }}
          >
            <Button
              ref={(el) => {
                deleteButtonRefs.current[record.id] = el;
              }}
              type="text"
              className={styles.hiddenButton}
            />
          </Popconfirm>}
          {canBlockPersonnel && <Popconfirm
            title={null}
            icon={null}
            description={record.status === 'active' 
              ? "Bạn có chắc chắn muốn khóa tài khoản này?"
              : "Bạn có chắc chắn muốn mở khóa tài khoản này?"}
            onConfirm={() => handleLockPersonnel(record)}
            okText={record.status === 'active' ? "Khóa" : "Mở khóa"}
            cancelText="Hủy"
            okButtonProps={{ 
              danger: record.status === 'active',
              loading: lockingId === record.id 
            }}
            placement="left"
            overlayStyle={{ marginLeft: '-20px' }}
            getPopupContainer={(triggerNode) => {
              const cell = triggerNode.closest('td');
              return cell || document.body;
            }}
          >
            <Button
              ref={(el) => {
                lockButtonRefs.current[record.id] = el;
              }}
              type="text"
              className={styles.hiddenButton}
            />
          </Popconfirm>}
          {(canReadPersonnel || canUpdatePersonnel || canDeletePersonnel || canBlockPersonnel) && <ActionMenu
            record={record}
            handlers={handleActionMenu}
            className={styles.actionButton}
            showItems={{
              view: canReadPersonnel,
              update: canUpdatePersonnel,
              approve: canUpdatePersonnel && usersWithPendingRequests.has(parseInt(record.id)),
              edit: canUpdatePersonnel,
              manageSession: false,
              lock: canBlockPersonnel,
              delete: canDeletePersonnel,
            }}
          />}
        </div>
      ),
    }] satisfies ColumnsType<Personnel>) : []),
  ];

  return (
    <div className={styles.container}>
      <FilterSection
        title="Bộ lọc"
        subtitle="Tìm kiếm và lọc dữ liệu chấm công"
        departments={filterDepartments}
        selectedDepartment={selectedDepartment}
        onDepartmentChange={setSelectedDepartment}
        searchPlaceholder="Tìm kiếm theo tên, mã NV, email, ..."
        searchValue={searchText}
        onSearchChange={setSearchText}
        onClearFilter={handleClearFilter}
        allDepartmentOptionText={allDepartmentsLabel}
        extraActions={canCreatePersonnel ? (
          <Button
            type="primary"
            icon={<img src={badgePlus} alt="add" className={styles.addIcon} />}
            size="large"
            onClick={handleAddPersonnel}
            className={styles.addButton}
          >
            Thêm nhân sự
          </Button>
        ) : undefined}
      />

      {error && (
        <Alert
          message="Lỗi"
          description={error}
          type="error"
          showIcon
          closable
          className={styles.errorAlert}
        />
      )}

      <div className={styles.tableContainer}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            pagination={false}
            className={styles.table}
            loading={loading}
          />
        </Spin>
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSizeChanger={true}
          showTotal={true}
          itemName="nhân viên"
        />
      </div>

      <AddPersonnelModal
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSubmit={handleSubmitAddPersonnel}
        departments={departments}
        roles={assignableRoles}
        managers={managerCandidates}
        canAssignRole={canAssignRoleOnCreate}
        fixedDepartmentId={fixedCreateDepartmentId}
      />

      <EditPersonnelModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEditPersonnel}
        departments={departments}
        roles={assignableRoles.length ? assignableRoles : undefined}
        managers={managerCandidates}
        canAssignRole={canAssignRole}
        editableFields={editablePersonnelFields}
        initialData={selectedPersonnel || undefined}
        initialEmail={selectedPersonnel?.email}
      />
    </div>
  );
};
